/**
 * Agape Energy Theme — homepage behaviors
 */
(function () {
  'use strict';

  function pickRandomItems(pool, count) {
    const remaining = pool.slice();
    const selected = [];

    for (let i = 0; i < count && remaining.length; i++) {
      const index = Math.floor(Math.random() * remaining.length);
      selected.push(remaining.splice(index, 1)[0]);
    }

    return selected;
  }

  function initHeroAvatars() {
    const container = document.querySelector('[data-hero-avatars]');
    if (!container || container.dataset.avatarsReady === 'true') return;

    const poolAttr = container.getAttribute('data-hero-avatar-pool');
    const baseUrl = container.getAttribute('data-hero-avatar-base') || '';
    if (!poolAttr || !baseUrl) return;

    const pool = poolAttr.split(',').map((name) => name.trim()).filter(Boolean);
    if (!pool.length) return;

    pickRandomItems(pool, Math.min(3, pool.length)).forEach((filename) => {
      const img = document.createElement('img');
      img.src = `${baseUrl}${filename}`;
      img.alt = 'Agape believer';
      img.decoding = 'async';
      img.loading = 'lazy';
      img.width = 32;
      img.height = 32;
      container.appendChild(img);
    });

    container.dataset.avatarsReady = 'true';
  }

  function initFlavorCarousel() {
    document.querySelectorAll('[data-flavor-showcase]').forEach((section) => {
      const track = section.querySelector('[data-flavor-carousel]');
      const dotsContainer = section.querySelector('[data-flavor-dots]');
      const prevBtn = section.querySelector('[data-flavor-prev]');
      const nextBtn = section.querySelector('[data-flavor-next]');
      if (!track || !dotsContainer) return;

      const slides = Array.from(track.querySelectorAll('[data-flavor-slide]'));
      if (!slides.length) return;

      function getOrderedSlides() {
        return slides
          .slice()
          .sort((a, b) => a.offsetLeft - b.offsetLeft);
      }

      let active = 0;

      function buildDots() {
        dotsContainer.innerHTML = '';
        getOrderedSlides().forEach((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'flavor-carousel__dot';
          dot.setAttribute('aria-label', slide.getAttribute('data-slide-label') || `Slide ${i + 1}`);
          dot.addEventListener('click', () => goTo(i));
          dotsContainer.appendChild(dot);
        });
      }

      function updateDots() {
        const orderedSlides = getOrderedSlides();
        dotsContainer.querySelectorAll('.flavor-carousel__dot').forEach((dot, i) => {
          const slide = orderedSlides[i];
          const color = slide ? slide.getAttribute('data-slide-color') : '#E8E6E1';
          dot.classList.toggle('active', i === active);
          dot.style.width = i === active ? '28px' : '8px';
          dot.style.background = i === active ? color : '#E8E6E1';
        });
      }

      function updateArrows() {
        const orderedSlides = getOrderedSlides();
        if (prevBtn) {
          prevBtn.disabled = active === 0;
          prevBtn.style.borderColor = active === 0 ? '#E8E6E1' : '#0D0D0D';
          prevBtn.style.color = active === 0 ? '#ccc' : '#0D0D0D';
        }
        if (nextBtn) {
          nextBtn.disabled = active === orderedSlides.length - 1;
          nextBtn.style.borderColor = active === orderedSlides.length - 1 ? '#E8E6E1' : '#0D0D0D';
          nextBtn.style.color = active === orderedSlides.length - 1 ? '#ccc' : '#0D0D0D';
        }
      }

      function goTo(index) {
        const orderedSlides = getOrderedSlides();
        active = Math.max(0, Math.min(index, orderedSlides.length - 1));
        const slide = orderedSlides[active];
        const offset = slide ? slide.offsetLeft : 0;
        track.scrollTo({ left: offset, behavior: 'smooth' });
        updateDots();
        updateArrows();
      }

      track.addEventListener('scroll', () => {
        const orderedSlides = getOrderedSlides();
        const idx = orderedSlides.findIndex((slide, i) => {
          const next = orderedSlides[i + 1];
          const slideStart = slide.offsetLeft;
          const slideEnd = next ? next.offsetLeft : track.scrollWidth;
          return track.scrollLeft >= slideStart - 4 && track.scrollLeft < slideEnd - 4;
        });
        const resolved = idx === -1 ? active : idx;
        if (resolved !== active) {
          active = resolved;
          updateDots();
          updateArrows();
        }
      }, { passive: true });

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(active - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(active + 1));

      buildDots();
      updateDots();
      updateArrows();
    });
  }

  let ingredientDetailsPromise;

  function getIngredientDetails() {
    if (!ingredientDetailsPromise) {
      const section = document.querySelector('[data-ingredients]');
      const url = section ? section.getAttribute('data-ingredient-details-url') : null;
      if (!url) {
        ingredientDetailsPromise = Promise.resolve({});
      } else {
        ingredientDetailsPromise = fetch(url)
          .then((response) => (response.ok ? response.json() : {}))
          .catch(() => ({}));
      }
    }
    return ingredientDetailsPromise;
  }

  function renderIngredientDetail(text) {
    return text
      .split('\n\n')
      .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
      .join('');
  }

  function initIngredientAccordion() {
    document.querySelectorAll('[data-ingredient-toggle]').forEach((toggle) => {
      const item = toggle.closest('.ingredient-item');
      const detail = item ? item.querySelector('[data-ingredient-detail]') : null;
      if (!detail) return;

      const setExpanded = async (expanded) => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.classList.toggle('is-expanded', expanded);
        detail.hidden = !expanded;

        if (!expanded || detail.dataset.loaded === 'true') return;

        const detailKey = detail.getAttribute('data-ingredient-key');
        if (!detailKey) return;

        const details = await getIngredientDetails();
        const text = details[detailKey];
        if (!text) return;

        const body = detail.querySelector('[data-ingredient-detail-body]');
        if (body) {
          body.innerHTML = renderIngredientDetail(text);
          detail.dataset.loaded = 'true';
        }
      };

      toggle.addEventListener('click', () => {
        setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
        }
      });
    });
  }

  function initPrimaryCtaPreload() {
    const primaryCta = document.querySelector('[data-hero] .btn-hero-primary[href]');
    if (!primaryCta) return;

    let targetUrl;
    try {
      targetUrl = new URL(primaryCta.href, window.location.href);
    } catch {
      return;
    }

    if (targetUrl.origin !== window.location.origin) return;
    if (!targetUrl.pathname || targetUrl.pathname === window.location.pathname) return;

    const normalizedHref = targetUrl.href;
    const preloadedHints = new Set();
    let prerenderScheduled = false;
    let fetchScheduled = false;

    function appendLink(rel, href, asValue) {
      const key = `${rel}:${href}`;
      if (preloadedHints.has(key)) return;

      const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
      if (existing) {
        preloadedHints.add(key);
        return;
      }

      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (asValue) link.as = asValue;
      document.head.appendChild(link);
      preloadedHints.add(key);
    }

    function schedulePrerender() {
      if (prerenderScheduled) return;
      if (!window.HTMLScriptElement || typeof window.HTMLScriptElement.supports !== 'function') return;
      if (!window.HTMLScriptElement.supports('speculationrules')) return;

      const script = document.createElement('script');
      script.type = 'speculationrules';
      script.textContent = JSON.stringify({
        prerender: [
          {
            source: 'list',
            urls: [normalizedHref]
          }
        ]
      });
      document.head.appendChild(script);
      prerenderScheduled = true;
    }

    function schedulePrefetch() {
      appendLink('prefetch', normalizedHref);
    }

    function warmDocumentCache() {
      if (fetchScheduled || !window.fetch) return;
      fetchScheduled = true;
      fetch(normalizedHref, {
        credentials: 'same-origin',
        mode: 'same-origin'
      }).catch(() => {});
    }

    function primeNavigation() {
      schedulePrerender();
      schedulePrefetch();
      warmDocumentCache();
    }

    if (window.AgapeTheme && window.AgapeTheme.runWhenIdle) {
      window.AgapeTheme.runWhenIdle(primeNavigation);
    } else {
      setTimeout(primeNavigation, 1);
    }

    ['pointerenter', 'focus', 'touchstart', 'mousedown'].forEach((eventName) => {
      primaryCta.addEventListener(eventName, primeNavigation, { passive: true, once: true });
    });
  }

  function completeHeroIntro() {
    if (document.body.dataset.heroIntroComplete === 'true') return;
    document.body.dataset.heroIntroComplete = 'true';
    document.dispatchEvent(new CustomEvent('agape:hero-intro-complete'));
  }

  function initHeroCountUp(video, endFrameBuffer) {
    const hero = video.closest('[data-hero]');
    if (!hero) return;

    const countEl = hero.querySelector('[data-count-up][data-sync-hero-video]');
    if (!countEl) return;

    const target = parseFloat(countEl.getAttribute('data-count-up') || '0');
    const decimals = parseInt(countEl.getAttribute('data-count-decimals') || '0', 10);
    const suffix = countEl.getAttribute('data-count-suffix') || '';
    const displayPlus = countEl.getAttribute('data-count-plus') === 'true';
    const fallbackDuration = parseInt(countEl.getAttribute('data-count-duration') || '2800', 10);
    const animateCount = window.AgapeTheme && window.AgapeTheme.animateCount;
    const formatCount = window.AgapeTheme && window.AgapeTheme.formatCount;
    let pendingStart = false;

    function getDurationMs() {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        return Math.round((video.duration - endFrameBuffer) * 1000);
      }
      return fallbackDuration;
    }

    function startCountUp() {
      if (countEl.dataset.countStarted === 'true') return;

      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        pendingStart = true;
        return;
      }

      countEl.dataset.countStarted = 'true';
      const durationMs = getDurationMs();
      if (animateCount) {
        animateCount(countEl, target, decimals, durationMs, suffix, displayPlus, completeHeroIntro);
      } else if (formatCount) {
        countEl.textContent = formatCount(target, decimals, suffix, displayPlus, target);
        completeHeroIntro();
      } else {
        countEl.textContent = String(target);
        completeHeroIntro();
      }
    }

    video.addEventListener('play', startCountUp, { once: true });
    video.addEventListener('loadedmetadata', () => {
      if (pendingStart) startCountUp();
    });
    video.addEventListener('hero-video-blocked', () => startCountUp(), { once: true });
  }

  function initHeroVideo() {
    document.querySelectorAll('[data-hero-video]').forEach((video) => {
      const wrap = video.closest('.hero__video-wrap');
      const END_FRAME_BUFFER = 0.12;
      initHeroCountUp(video, END_FRAME_BUFFER);
      let isFrozen = false;
      let freezeFrame = null;

      function getFreezeFrame() {
        if (freezeFrame) return freezeFrame;

        freezeFrame = document.createElement('canvas');
        freezeFrame.className = 'hero__freeze-frame';
        freezeFrame.setAttribute('aria-hidden', 'true');
        freezeFrame.style.position = 'absolute';
        freezeFrame.style.inset = '0';
        freezeFrame.style.width = '100%';
        freezeFrame.style.height = '100%';
        freezeFrame.style.objectFit = 'cover';
        freezeFrame.style.objectPosition = 'center top';
        freezeFrame.style.pointerEvents = 'none';
        freezeFrame.style.display = 'none';
        freezeFrame.style.zIndex = '2';

        const parent = video.parentElement;
        if (parent) parent.appendChild(freezeFrame);

        return freezeFrame;
      }

      function syncFreezeFrameStyles(canvas) {
        const computedStyle = window.getComputedStyle(video);
        canvas.style.objectFit = computedStyle.objectFit || 'cover';
        canvas.style.objectPosition = computedStyle.objectPosition || 'center top';
        canvas.style.transform = computedStyle.transform || 'none';
        canvas.style.transformOrigin = computedStyle.transformOrigin || 'center top';
      }

      function hideFreezeFrame() {
        if (!freezeFrame) return;
        freezeFrame.style.display = 'none';
      }

      function captureFreezeFrame() {
        if (!video.videoWidth || !video.videoHeight) return false;

        const canvas = getFreezeFrame();
        syncFreezeFrameStyles(canvas);
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        if (!context) return false;

        try {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.style.display = 'block';
          return true;
        } catch (error) {
          return false;
        }
      }

      video.loop = false;

      video.addEventListener('play', () => {
        isFrozen = false;
        hideFreezeFrame();
        if (wrap) {
          wrap.classList.remove('is-video-pending');
          wrap.classList.add('is-video-active');
        }
      });

      video.addEventListener('loadedmetadata', () => {
        hideFreezeFrame();
        isFrozen = false;
      });

      function freezeVideo() {
        if (isFrozen) return;

        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        if (!duration || video.currentTime < duration - END_FRAME_BUFFER) return;

        captureFreezeFrame();
        video.pause();
        isFrozen = true;
        completeHeroIntro();
      }

      video.addEventListener('timeupdate', freezeVideo);
      video.addEventListener('ended', () => {
        captureFreezeFrame();
        freezeVideo();
      });

      function maybeStartVideo() {
        if (!video.paused) return;

        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');

        if (wrap) wrap.classList.add('is-video-pending');

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            video.dispatchEvent(new CustomEvent('hero-video-blocked'));
            if (wrap && video.paused) wrap.classList.remove('is-video-pending');
          });
        }
      }

      maybeStartVideo();
      video.addEventListener('canplay', maybeStartVideo, { once: true });
    });
  }

  function init() {
    initFlavorCarousel();
    initIngredientAccordion();
    initPrimaryCtaPreload();
    initHeroVideo();

    const runAvatars = () => initHeroAvatars();
    if (window.AgapeTheme && window.AgapeTheme.runWhenIdle) {
      window.AgapeTheme.runWhenIdle(runAvatars);
    } else {
      setTimeout(runAvatars, 1);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
